import { useFirePostOfficeAnalyticsEvents } from '@post-office/analytics';
import { type ReactNode, useEffect, useState } from 'react';
import { IntlProvider, useIntl } from 'react-intl-next';

type LoadI18nMessagesResult = {
	locale: string;
	messages: Record<string, string> | undefined;
};

export type I18nMessageLoader = (
	messageTemplateId: string,
	placementId: string,
	locale: string,
) => Promise<{ default: Record<string, string> }>;

/**
 * Dynamically loads i18n translation messages for a placement at runtime.
 *
 * WHY DYNAMIC IMPORT:
 * Translation packages (@post-office/i18n-translation/*) are only installed for
 * specific messageTemplate/placement combinations in the monorepo. Attempting to
 * bundle all possible translations would be prohibitively large (~1000s of packages).
 * Dynamic import allows loading only the messages needed by the active placement.
 *
 * SAFETY & REQUIREMENTS:
 * ⚠️  The `webpackIgnore: true` comment tells the bundler to skip static analysis
 *     of this dynamic import. This *only* remains safe if:
 *     1. messageTemplateId and placementId come exclusively from verified placement config
 *        (not user input, URL params, or untrusted sources)
 *     2. The imported package is guaranteed to exist at runtime (placement deployment validates this)
 *     3. The import follows the strict convention: @post-office/i18n-translation/{messageTemplateId}-in-app/{placementId}
 *
 * RISKS IF REQUIREMENTS BREAK:
 *     • Module not found errors at runtime (placement stops loading)
 *     • Silent failures if fallback import path doesn't exist
 *     • XSS or code injection if messageTemplateId/placementId become user-controlled
 *
 * @param messageTemplateId - Message template ID from placement deployment config (hardcoded per placement)
 * @param placementId - Placement ID from placement deployment config (hardcoded per placement)
 * @param _locale - Current locale preference (unused in dynamic path but part of interface contract)
 * @returns Promise resolving to i18n message record for the placement
 * @throws Will throw if translation package is not installed or module resolution fails
 */
export const loadPlacementMessages = async (
	messageTemplateId: string,
	placementId: string,
	_locale: string,
): Promise<{ default: Record<string, string> }> => {
	const candidate = `@post-office/i18n-translation/${messageTemplateId}-in-app/${placementId}`;
	// eslint-disable-next-line @typescript-eslint/no-unsafe-return
	return await import(/* webpackIgnore: true */ candidate);
};

/**
 * This react hook loads the messages for a given locale from the NPM package.
 * It returns the locale and the messages.
 * @param messageTemplate
 * @param placement
 * @param messageLoader
 * @returns {locale: string, messages: Record<string, string> | undefined}
 */
export function useLoadI18nMessages(
	messageTemplateId: string,
	placementId: string,
	messageLoader: I18nMessageLoader = loadPlacementMessages,
): LoadI18nMessagesResult {
	const [messages, setMessages] = useState<Record<string, string> | undefined>();
	const { fireAnalyticsEvent } = useFirePostOfficeAnalyticsEvents();

	let locale = 'en';
	try {
		locale = useIntl().locale;
	} catch {
		// fallback to en
	}

	useEffect(() => {
		messageLoader(messageTemplateId, placementId, locale)
			.then((loadedMessages) => {
				setMessages(loadedMessages.default);
			})
			.catch(() => {
				setMessages({});
				fireAnalyticsEvent({
					action: 'error',
					actionSubject: 'i18nMessageLoader',
					eventType: 'operational',
				});
			});
	}, [fireAnalyticsEvent, locale, messageLoader, messageTemplateId, placementId]);

	return { locale, messages };
}

type I18nProviderProps = {
	children: ReactNode;
	messageTemplateId: string;
	placementId: string;
	messageLoader?: I18nMessageLoader;
};

const I18nProvider = ({
	children,
	messageTemplateId,
	placementId,
	messageLoader,
}: I18nProviderProps): JSX.Element => {
	const { locale, messages } = useLoadI18nMessages(messageTemplateId, placementId, messageLoader);
	if (!messages || Object.keys(messages).length === 0) {
		return <>{children}</>;
	}
	return (
		<IntlProvider locale={locale} messages={messages}>
			{children}
		</IntlProvider>
	);
};

type MaybeI18nProviderProps = {
	isI18nMigrationInAppEnabled: boolean;
	messageTemplateId: string;
	placementId: string | undefined;
	children: ReactNode;
	messageLoader?: I18nMessageLoader;
};

export const MaybeI18nProvider = ({
	isI18nMigrationInAppEnabled,
	messageTemplateId,
	placementId,
	children,
	messageLoader = loadPlacementMessages,
}: MaybeI18nProviderProps): JSX.Element => {
	if (!isI18nMigrationInAppEnabled || !placementId) {
		return <>{children}</>;
	}
	return (
		<I18nProvider
			messageTemplateId={messageTemplateId}
			placementId={placementId}
			messageLoader={messageLoader}
		>
			{children}
		</I18nProvider>
	);
};
